import React, { useState, useEffect, useRef } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { getSpeechLabAccessToken } from './api'
import { getTranscriptText } from '../Utils/transcriptTextFormatter'
import { makeTimestampsClickable, handleTimestampLinkClick } from '../Utils/timestampLinks'
import DOMPurify from 'dompurify'
import { FaInfoCircle, FaChevronUp, FaChevronDown, FaFileAlt, FaCopy, FaSlidersH } from 'react-icons/fa'
import './SummaryTab.css'

const MAX_CONSECUTIVE_POLL_ERRORS = 3
const TIMEOUT_DURATION = 180000 // 3 minutes
const POLL_INTERVAL_DURATION = 3000 // 3 seconds

const SummaryTab = ({ transcriptData, transcriptXMLPath, fileName, mediaId, onTimestampClick }) => {
    const [loading, setLoading] = useState(false)
    const [summary, setSummary] = useState('')
    const [previousSummary, setPreviousSummary] = useState('')
    const [taskId, setTaskId] = useState(null)
    const [isPolling, setIsPolling] = useState(false)
    const [pollTimeoutReached, setPollTimeoutReached] = useState(false)
    const [summaryFormat, setSummaryFormat] = useState('default')
    const [customPrompt, setCustomFormat] = useState('')
    const [showFormatOptions, setShowFormatOptions] = useState(false)
    const [footerCollapsed, setFooterCollapsed] = useState(false)

    const pollIntervalRef = useRef(null)
    const pollTimeoutRef = useRef(null)
    const isRequestInFlightRef = useRef(false)
    const consecutiveErrorCountRef = useRef(0)

    const stopPolling = () => {
        if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current)
            pollIntervalRef.current = null
        }
        if (pollTimeoutRef.current) {
            clearTimeout(pollTimeoutRef.current)
            pollTimeoutRef.current = null
        }
        isRequestInFlightRef.current = false
        consecutiveErrorCountRef.current = 0
    }

    // reset states when component unmounts
    useEffect(() => {
        return () => {
            stopPolling()
            setLoading(false)
            setPollTimeoutReached(false)
            setTaskId(null)
            setSummary('')
            setPreviousSummary('')
        }
    }, [])

    // load existing summary and task status when component mounts
    useEffect(() => {
        const loadExistingData = async () => {
            if (mediaId) {
                try {
                    const accessToken = await getSpeechLabAccessToken()
                    const taskStatusResponse = await axios.get(
                        `${process.env.REACT_APP_STUDIO_URL}/api/ai/summary-task-status?mediaId=${mediaId}`,
                        {
                            headers: {
                                Authorization: `Bearer ${accessToken}`
                            }
                        }
                    )
                    const { taskId, status, createdAt } = taskStatusResponse.data
                    const summaryResponse = await axios.get(
                        `${process.env.REACT_APP_STUDIO_URL}/api/ai/summary?mediaId=${mediaId}`,
                        {
                            headers: {
                                Authorization: `Bearer ${accessToken}`
                            }
                        }
                    )

                    if (taskId && status === 'processing') {
                        setTaskId(taskId)
                        setPollTimeoutReached(true)
                        if (summaryResponse.data.summary) {
                            setPreviousSummary(summaryResponse.data.summary)
                            setSummary('')
                        }
                        toast.info('Summary generation in progress')
                    } else {
                        // No active task, show existing summary
                        if (summaryResponse.data.summary) {
                            setSummary(summaryResponse.data.summary)
                        }
                    }
                } catch (error) {
                    console.error('Error loading existing data:', error)
                }
            }
        }
        loadExistingData()
    }, [mediaId])

    const startPolling = (taskId, accessToken) => {
        pollIntervalRef.current = setInterval(async () => {
            if (isRequestInFlightRef.current) return
            isRequestInFlightRef.current = true
            try {
                const pollResponse = await axios.get(
                    `${process.env.REACT_APP_STUDIO_URL}/api/ai/poll/${taskId}?mediaId=${mediaId}`,
                    {
                        headers: {
                            Authorization: `Bearer ${accessToken}`
                        }
                    }
                )
                consecutiveErrorCountRef.current = 0
                if (pollResponse.data.status === 'done') {
                    stopPolling()
                    setIsPolling(false)
                    setSummary(pollResponse.data.summary)
                    toast.success('Summary generated successfully!')
                    setLoading(false)
                } else if (pollResponse.data.status === 'error') {
                    stopPolling()
                    setIsPolling(false)
                    toast.error('Failed to generate summary')
                    setLoading(false)
                }
            } catch (error) {
                console.error('Error polling:', error)
                consecutiveErrorCountRef.current += 1
                if (consecutiveErrorCountRef.current >= MAX_CONSECUTIVE_POLL_ERRORS) {
                    stopPolling()
                    setIsPolling(false)
                    setLoading(false)
                    setPollTimeoutReached(true)
                    toast.error('Connection lost while checking summary status')
                }
            } finally {
                isRequestInFlightRef.current = false
            }
        }, POLL_INTERVAL_DURATION)
    }

    const startPollingTimeout = () => {
        pollTimeoutRef.current = setTimeout(() => {
            stopPolling()
            setIsPolling(false)
            setPollTimeoutReached(true)
            setLoading(false)
            toast.error('Summary generation in progress', { icon: '🕑', duration: 3000 })
        }, TIMEOUT_DURATION)
    }

    const initiateSummaryTask = async (transcriptText, accessToken) => {
        const userPrompt = summaryFormat === 'custom' && customPrompt
            ? `Summarize the session according to the following instructions: ${customPrompt}`
            : undefined

        const response = await axios.post(
            `${process.env.REACT_APP_STUDIO_URL}/api/ai/summarize`,
            {
                transcript: transcriptText,
                fileName,
                mediaId,
                userPrompt
            },
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`
                }
            }
        )
        return response.data.taskId
    }

    const handleSummarize = async () => {
        setLoading(true)
        setPollTimeoutReached(false)
        setTaskId(null)
        setPreviousSummary(summary)
        setSummary('')
        stopPolling()

        try {
            const transcriptText = getTranscriptText(transcriptData, transcriptXMLPath)
            const accessToken = await getSpeechLabAccessToken()
            const newTaskId = await initiateSummaryTask(transcriptText, accessToken)
            setTaskId(newTaskId)
            setIsPolling(true)
            startPolling(newTaskId, accessToken)
            startPollingTimeout()
        } catch (error) {
            console.error('Error generating summary:', error)
            toast.error('Failed to generate summary. Please try again')
            setLoading(false)
        }
    }

    const handlePollToast = (promise) => {
        return toast.promise(
            promise,
            {
                loading: 'Checking summary status...',
                success: (response) => {
                    if (response.data.status === 'done') {
                        setSummary(response.data.summary)
                        setPollTimeoutReached(false)
                        setTaskId(null)
                        return 'Summary generated successfully'
                    } else if (response.data.status === 'error') {
                        setSummary('Failed to generate summary')
                        setPollTimeoutReached(false)
                        setTaskId(null)
                        throw new Error('Failed to generate summary')
                    } else {
                        setSummary('Summary generation in progress.')
                        throw new Error('Summary generation in progress')
                    }
                },
                error: (err) => {
                    setSummary('Failed to poll summary status')
                    return err.message || 'Failed to poll summary status'
                }
            },
            {
                loading: {
                    duration: 3000,
                },
                error: {
                    duration: 3000,
                    icon: '🕑',
                }
            }
        )
    }

    const handleManualPoll = async () => {
        if (!taskId) return

        setLoading(true)
        setSummary('Checking summary status')

        const pollPromise = (async () => {
            const accessToken = await getSpeechLabAccessToken()
            const response = await axios.get(
                `${process.env.REACT_APP_STUDIO_URL}/api/ai/poll/${taskId}?mediaId=${mediaId}`,
                {
                    headers: {
                        Authorization: `Bearer ${accessToken}`
                    }
                }
            )
            await new Promise(resolve => setTimeout(resolve, 1000))
            return response
        })()

        handlePollToast(pollPromise).finally(() => {
            setLoading(false)
        })
    }

    const hasResult = summary || (previousSummary && (loading || isPolling || pollTimeoutReached))
    const isEmpty = !hasResult && !loading && !isPolling && !pollTimeoutReached
    const displayedSummary = summary || previousSummary
    const showCopyButton = Boolean(displayedSummary) && !isEmpty

    const handleCopySummary = async () => {
        const summaryToCopy = summary || previousSummary
        if (!summaryToCopy) return

        try {
            // remove html tags and convert to plain text
            const tempDiv = document.createElement('div')
            tempDiv.innerHTML = summaryToCopy
            let plainText = tempDiv.textContent || tempDiv.innerText || ''

            // strip markdown syntax
            plainText = plainText
                .replace(/#{1,6}\s/g, '') // Remove headers
                .replace(/\*\*/g, '') // Remove bold
                .replace(/\*/g, '') // Remove italic
                .replace(/`/g, '') // Remove code
                .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Remove links, keep text
                .replace(/\n{3,}/g, '\n\n') // Normalize multiple newlines to double
                .trim()

            await navigator.clipboard.writeText(plainText)
            toast.success('Summary copied to clipboard')
        } catch (error) {
            console.error('Failed to copy summary:', error)
            toast.error('Failed to copy summary')
        }
    }

    return (
        <div className="summary-tab-content">
            <div className={`summary-scroll-area ${isEmpty ? 'summary-scroll-area--empty' : ''}`}>
                {pollTimeoutReached && (
                    <div className="ai-status-container">
                        <div className="ai-status-message">
                            <FaInfoCircle />
                            Summary generation in progress
                        </div>
                        <button
                            className="btn btn-secondary"
                            onClick={handleManualPoll}
                            disabled={loading}
                        >
                            {loading ? 'Checking...' : 'Check Status'}
                        </button>
                    </div>
                )}
                {loading || isPolling || pollTimeoutReached ? (
                    previousSummary ? (
                        <>
                            {(loading || isPolling) && !pollTimeoutReached && (
                                <div className="ai-result" style={{ opacity: 0.6, marginBottom: 12 }}>
                                    <div className="ai-status-message">
                                        <span className="spinner-small"></span>
                                        Generating new summary...
                                    </div>
                                </div>
                            )}
                            <div className="ai-result" style={{ opacity: 0.6 }}>
                                <div className="ai-result-header">
                                    <h3>Previous Summary</h3>
                                </div>
                                <div
                                    className="summary-body"
                                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(makeTimestampsClickable(previousSummary)) }}
                                    onClick={(e) => handleTimestampLinkClick(e, onTimestampClick)}
                                />
                            </div>
                        </>
                    ) : (
                        <div className="summary-generating-state">
                            <span className="spinner"></span>
                            <p>Generating summary...</p>
                            <p className="summary-generating-subtitle">
                                This may take a few moments.
                            </p>
                        </div>
                    )
                ) : summary ? (
                    <div className="ai-result">
                        <div className="ai-result-header">
                            <h3>Summary</h3>
                            <button
                                type="button"
                                className="copy-summary-btn"
                                onClick={handleCopySummary}
                                aria-label="Copy summary"
                                title="Copy summary"
                            >
                                <FaCopy />
                            </button>
                        </div>
                        <div
                            className="summary-body"
                            dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(makeTimestampsClickable(summary))
                            }}
                            onClick={(e) => handleTimestampLinkClick(e, onTimestampClick)}
                        />
                    </div>
                ) : null}
                {isEmpty && (
                    <div className="summary-generate-section">
                        <div className="summary-format-section">
                            <label className="format-label">Summary Format:</label>
                            <select
                                className="format-select"
                                value={summaryFormat}
                                onChange={(e) => setSummaryFormat(e.target.value)}
                            >
                                <option value="default">Default</option>
                                <option value="custom">Custom</option>
                            </select>
                            {summaryFormat === 'custom' && (
                                <>
                                    <label className="format-label">
                                        Custom summary instructions:
                                    </label>
                                    <input
                                        type="text"
                                        className="format-input"
                                        placeholder="e.g. bullet points, executive summary, timestamps, timeline"
                                        value={customPrompt}
                                        onChange={(e) => setCustomFormat(e.target.value)}
                                    />
                                </>
                            )}
                        </div>
                        <button
                            type="button"
                            className="btn btn-primary summary-empty-cta"
                            onClick={handleSummarize}
                            disabled={summaryFormat === 'custom' && !customPrompt.trim()}
                        >
                            Generate Summary
                        </button>
                    </div>
                )}
                {isEmpty && (
                    <div className="summary-empty-state">
                        <FaFileAlt className="summary-empty-icon" />
                        <p className="summary-empty-title">No summary yet</p>
                        <p className="summary-empty-subtitle">
                            Generate a summary of this transcript to get started.
                        </p>
                    </div>
                )}
            </div>
            {!pollTimeoutReached && !isEmpty && (
                <div className={`summary-action-bar ${footerCollapsed ? 'collapsed' : ''}`}>
                    <button
                        type="button"
                        className="footer-collapse-handle"
                        onClick={() => setFooterCollapsed(prev => !prev)}
                        aria-label={footerCollapsed ? 'Expand generate options' : 'Collapse generate options'}
                        title={footerCollapsed ? 'Expand generate options' : 'Collapse generate options'}
                    >
                        {footerCollapsed ? <FaChevronUp /> : <FaChevronDown />}
                        <span>{footerCollapsed ? 'Generate summary' : 'Hide'}</span>
                    </button>
                    {!footerCollapsed && (
                        <div className="summary-action-body">
                            {showFormatOptions && !loading && !isPolling && (
                                <div className="summary-format-section">
                                    <div className="format-section-header">
                                        <label className="format-label">Summary Format:</label>
                                        <button
                                            type="button"
                                            className="format-close-btn"
                                            onClick={() => setShowFormatOptions(false)}
                                            aria-label="Close format options"
                                        >
                                            ×
                                        </button>
                                    </div>
                                    <select
                                        className="format-select"
                                        value={summaryFormat}
                                        onChange={(e) => setSummaryFormat(e.target.value)}
                                        disabled={loading || isPolling}
                                    >
                                        <option value="default">Default</option>
                                        <option value="custom">Custom</option>
                                    </select>
                                    {summaryFormat === 'custom' && (
                                        <>
                                            <label className="format-label">
                                                Custom summary instructions:
                                            </label>
                                            <input
                                                type="text"
                                                className="format-input"
                                                placeholder="e.g. bullet points, executive summary, timestamps, timeline"
                                                value={customPrompt}
                                                onChange={(e) => setCustomFormat(e.target.value)}
                                                disabled={loading || isPolling}
                                            />
                                        </>
                                    )}
                                </div>
                            )}
                            <div className="summary-action-row">
                                <button
                                    type="button"
                                    className={`format-options-toggle ${showFormatOptions ? 'active' : ''}`}
                                    onClick={() => setShowFormatOptions(prev => !prev)}
                                    disabled={loading || isPolling}
                                    aria-label="Summary format options"
                                    title="Summary format options"
                                >
                                    <FaSlidersH />
                                </button>
                                <button
                                    className="btn btn-primary"
                                    onClick={handleSummarize}
                                    disabled={loading || isPolling || (summaryFormat === 'custom' && !customPrompt.trim())}
                                >
                                    {loading || isPolling ? (
                                        <span className="loading-text">
                                            <span className="spinner"></span>
                                            Generating...
                                        </span>
                                    ) : summary ? 'Generate New Summary' : 'Generate Summary'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

export default SummaryTab